import React, { useEffect, useState, useCallback } from 'react';
import { Box, Button, Heading, Text, VStack, HStack, Icon } from '@chakra-ui/react';
import { FiDownload } from 'react-icons/fi';
import { App } from '@capacitor/app';
import { checkAppVersion } from '../lib/appVersion';
import { openUrl, isNativePlatform } from '../lib/platformUtils';
import { tt } from "../i18n/tt";

const HIDE_KEY = 'appUpdateHideUntil';

const isHiddenForNow = () => {
  try {
    const until = localStorage.getItem(HIDE_KEY);
    if (!until) return false;
    return new Date() < new Date(until);
  } catch {
    return false;
  }
};

/**
 * App-wide native update gate. Renders nothing on web or when up to date.
 * - required: full-screen, non-dismissible block with an update button.
 * - recommended: dismissible notice with a "오늘 그만보기" (24h) option.
 */
const ForceUpdateGate = () => {
  const [gate, setGate] = useState({ status: 'ok' });

  const evaluate = useCallback(async () => {
    const result = await checkAppVersion();
    if (result.status === 'recommended' && isHiddenForNow()) {
      setGate({ status: 'ok' });
      return;
    }
    setGate(result);
  }, []);

  useEffect(() => {
    evaluate();
    if (!isNativePlatform()) return undefined;

    // Re-check when the app returns to the foreground (user may have updated,
    // or admin may have raised the minimum version while the app stayed open).
    let handle;
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) evaluate();
    }).then((h) => {
      handle = h;
    });
    return () => {
      if (handle) handle.remove();
    };
  }, [evaluate]);

  if (gate.status === 'ok') return null;

  const required = gate.status === 'required';

  const handleUpdate = () => {
    if (gate.storeUrl) openUrl(gate.storeUrl);
  };
  const handleLater = () => setGate({ status: 'ok' });
  const handleHide24 = () => {
    try {
      const until = new Date(Date.now() + 24 * 60 * 60 * 1000);
      localStorage.setItem(HIDE_KEY, until.toISOString());
    } catch {
      /* localStorage unavailable — just dismiss for this session */
    }
    setGate({ status: 'ok' });
  };

  const defaultMessage = required
    ? tt("a.9047a3d4", "원활한 서비스 이용을 위해 최신 버전으로 업데이트해 주세요. 업데이트 후 다시 이용하실 수 있습니다.")
    : tt("a.9f54a149", "새로운 기능과 개선 사항이 포함된 최신 버전이 있습니다. 업데이트를 권장합니다.");

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bg="rgba(0, 0, 0, 0.7)"
      zIndex={9999}
      display="flex"
      alignItems="center"
      justifyContent="center"
      p={4}
    >
      <Box
        w="90%"
        maxW="400px"
        bg="white"
        borderRadius="12px"
        border="1px solid"
        borderColor="neutral.200"
        boxShadow="2xl"
        p={{ base: 6, md: 8 }}
      >
        <VStack spacing={5} align="stretch">
          <VStack spacing={3}>
            <Box
              w="56px"
              h="56px"
              borderRadius="full"
              bg="brand.50"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={FiDownload} boxSize="28px" color="brand.500" />
            </Box>
            <Heading size="md" textAlign="center" color="neutral.900">
              {required ? tt("a.13438a0b", "업데이트가 필요합니다") : tt("a.768bbd94", "새 버전이 있습니다")}
            </Heading>
          </VStack>

          <Text fontSize="sm" color="neutral.600" textAlign="center" lineHeight="1.6">
            {gate.message || defaultMessage}
          </Text>

          {gate.latestVersion && (
            <Text fontSize="xs" color="neutral.400" textAlign="center">{tt("a.a3dc8f2e", "최신 버전 {{v0}}", { v0: gate.latestVersion })}</Text>
          )}

          <Button colorScheme="brand" size="lg" w="100%" onClick={handleUpdate}>
            {tt("a.93ae047c", "지금 업데이트")}
          </Button>

          {!required && (
            <HStack spacing={2} justify="center">
              <Button variant="ghost" size="sm" color="neutral.500" onClick={handleLater}>
                {tt("a.8c54e46b", "다음에")}
              </Button>
              <Button variant="ghost" size="sm" color="neutral.500" onClick={handleHide24}>
                {tt("a.3ea14ade", "오늘 그만보기")}
              </Button>
            </HStack>
          )}
        </VStack>
      </Box>
    </Box>
  );
};

export default ForceUpdateGate;
